import { useCallback, useEffect, useState } from 'react';

/**
 * Light or dark, chosen by the customer.
 *
 * LIGHT IS THE DEFAULT AND THE OS IS NOT CONSULTED. The application previously keyed
 * off `prefers-color-scheme`, so anyone whose machine was set to dark met a dark
 * storefront with no way back. The theme is now an explicit choice, remembered per
 * browser.
 *
 * The attribute is stamped on <html> before first paint by a small script in index.html;
 * this hook keeps React in step with it afterwards. Storage access is wrapped because it
 * throws outright in some privacy modes, and a blocked read must degrade to light rather
 * than break the page.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'mm-theme';

function readStoredTheme(): Theme {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === 'dark') root.setAttribute('data-theme', 'dark');
  else root.removeAttribute('data-theme');
}

export function useTheme(): { theme: Theme; toggleTheme: () => void } {
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme());

  // Keeps the DOM authoritative even if the attribute is changed elsewhere.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next: Theme = current === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // The preference will not survive this session. The toggle still works.
      }
      return next;
    });
  }, []);

  return { theme, toggleTheme };
}
